import { memo, useEffect, useRef, useState } from 'react';
import { View, Pressable, Platform, Animated, type ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText } from '@/components/ui/Text';

type WalletTab = 'financial' | 'analytics';

type WalletTabBarProps = {
  activeTab: WalletTab;
  onTabChange: (tab: WalletTab) => void;
};

const TABS: Array<{
  key: WalletTab;
  label: string;
  iconActive: keyof typeof Ionicons.glyphMap;
  iconInactive: keyof typeof Ionicons.glyphMap;
}> = [
  { key: 'financial', label: 'Budget', iconActive: 'wallet', iconInactive: 'wallet-outline' },
  { key: 'analytics', label: 'Insights', iconActive: 'stats-chart', iconInactive: 'stats-chart-outline' },
];

export const WalletTabBar = memo(function WalletTabBar({ activeTab, onTabChange }: WalletTabBarProps) {
  const theme = useTheme();
  const containerRef = useRef<View>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const indicatorTranslate = useRef(new Animated.Value(0)).current;
  const [indicatorLeft, setIndicatorLeft] = useState(0);

  // Animation values for active tab indicator
  const activeIndex = TABS.findIndex(tab => tab.key === activeTab);

  useEffect(() => {
    if (containerWidth === 0) {
      return;
    }

    const tabWidth = containerWidth / TABS.length;
    const target = tabWidth * activeIndex;

    if (Platform.OS === 'web') {
      // Smooth transition on web
      setIndicatorLeft(target);
      indicatorTranslate.setValue(target);
    } else {
      Animated.spring(indicatorTranslate, {
        toValue: target,
        damping: 20,
        stiffness: 200,
        mass: 0.8,
        useNativeDriver: true,
      }).start();
    }
  }, [activeIndex, containerWidth, indicatorTranslate, activeTab]);

  return (
    <View
      ref={containerRef}
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        if (width > 0) {
          setContainerWidth(width);
          const tabWidth = width / TABS.length;
          const initialPosition = tabWidth * activeIndex;
          indicatorTranslate.setValue(initialPosition);
          if (Platform.OS === 'web') {
            setIndicatorLeft(initialPosition);
          }
        }
      }}
      style={{
        flexDirection: 'row',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.radii.xl,
        padding: theme.spacing(0.75),
        borderWidth: 1,
        borderColor: theme.colors.borderMuted,
        position: 'relative',
        zIndex: 1,
        // Keep the tab bar flat to avoid visible gradient cuts
        ...(Platform.OS === 'web' ? {
          boxShadow: undefined,
        } : {
          shadowColor: 'transparent',
          shadowOffset: { width: 0, height: 0 },
          shadowOpacity: 0,
          shadowRadius: 0,
          elevation: 0,
        }),
      }}
    >
      {/* Animated background indicator */}
      {containerWidth > 0 && (
        Platform.OS === 'web' ? (
          <View
            key={`indicator-${activeTab}-${activeIndex}`}
            style={
              {
                position: 'absolute',
                top: theme.spacing(0.5),
                bottom: theme.spacing(0.5),
                left: indicatorLeft + theme.spacing(0.75),
                width: `calc(${100 / TABS.length}% - ${theme.spacing(1.5)}px)`,
                backgroundColor: theme.colors.primary,
                borderRadius: theme.radii.lg,
                pointerEvents: 'none',
                transition: 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              } as unknown as ViewStyle
            }
          />
        ) : (
          <Animated.View
            key={`indicator-${activeTab}-${activeIndex}`}
            style={[
              {
                position: 'absolute',
                top: theme.spacing(0.5),
                bottom: theme.spacing(0.5),
                left: theme.spacing(0.75),
                width: `${100 / TABS.length}%`,
                backgroundColor: theme.colors.primary,
                borderRadius: theme.radii.lg,
                pointerEvents: 'none',
                transform: [
                  {
                    translateX: indicatorTranslate,
                  },
                ],
              },
            ]}
          />
        )
      )}
      
      {TABS.map((tab) => {
        const isActive = activeTab === tab.key;
        return (
          <Pressable
            key={tab.key}
            onPress={() => {
              onTabChange(tab.key);
            }}
            style={({ pressed }) => ({
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: theme.spacing(1),
              paddingVertical: theme.spacing(1.75),
              paddingHorizontal: theme.spacing(2),
              borderRadius: theme.radii.lg,
              zIndex: 10,
              position: 'relative',
              opacity: pressed ? 0.8 : 1,
            })}
          >
            <Ionicons
                name={isActive ? tab.iconActive : tab.iconInactive}
                size={isActive ? 22 : 18}
                color={isActive ? (theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF') : theme.colors.textSecondary}
            />
            <ThemedText
              variant="bodyStrong"
              style={{
                fontSize: isActive ? 16 : 14,
                fontWeight: isActive ? '700' : '500',
                color: isActive ? (theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF') : theme.colors.textSecondary,
                letterSpacing: isActive ? 0.2 : 0,
              }}
            >
              {tab.label}
            </ThemedText>
          </Pressable>
        );
      })}
    </View>
  );
});

